/**
 * Interest reserve + closing costs, each FINANCED or BORROWER CASH — the RTL
 * and GUC sizers' per-property "where do these dollars live" questions
 * (pricing-scenario-sizers.md §12b, owner 2026-10-30).
 *
 * One convention for the three items, the construction holdback already
 * following it:
 *   • interest reserve — financed lives INSIDE the base loan amount (in leverage
 *     sizing mode the server grosses the base up by it); borrower cash is
 *     `requirements.liquidityComponents.borrowerFundedInterestReserve`.
 *   • closing costs — financed ALSO lives inside the base loan amount (owner
 *     2026-11-01, reversing the 10-30 "on the total" rule): grossed up in
 *     leverage mode, an entered amount taken as inclusive; borrower cash is
 *     cash to close in required liquidity (assignment fees are always cash).
 *
 * Every dollar here is the server's: the reserve's required months and amount
 * come back in `result.interestReserve`, the applied reserve per property in
 * `result.derivedInterestReserves[]`, and the closing-costs split (with its
 * "estimated 5% of purchase price" basis) in `result.closingCosts.perProperty[]`.
 * The form only asks the questions.
 */
import { useEffect } from "react";
import { useField, useForm } from "react-final-form";

import Box from "@mui/material/Box";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  SZ,
  Grid,
  Field2,
  RffBoolSelect,
  RffMoney,
  RffSelect,
  SubLabel,
} from "./sizerUi";
import { fmtMoney } from "./sizerFormat";

/**
 * `interestReserveMonths` — the model's own `ge=1, le=12` bound. The blank row
 * is NOT "no reserve": once the funding question is answered the server applies
 * the guideline's required months for the borrower class (`monthsSource:
 * "required_default"`), so blank means "use the required months".
 */
export const optionsInterestReserveMonths = [
  { value: null, label: "Required months" },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} month${i ? "s" : ""}`,
  })),
];

/** `financedReserve` — a tri-state: unanswered is not "no". */
export const optionsFinancedReserve = [
  { value: true, label: "Loan proceeds (financed)" },
  { value: false, label: "Borrower cash at closing" },
];

/** `financedClosingCosts` — the same tri-state. */
export const optionsFinancedClosingCosts = [
  { value: true, label: "Financed into the loan" },
  { value: false, label: "Borrower cash at closing" },
];

const isBlank = (v) => v == null || v === "";
const answered = (v) => v === true || v === false;

/** A tinted one-line note under a block. */
const Note = ({ tone = "info", children }) => (
  <Flx
    ac
    g={1}
    sx={{
      mt: "14px",
      p: "9px 12px",
      borderRadius: SZ.radiusSm,
      background: tone === "warn" ? SZ.warnBg : SZ.teal50,
      border: `1px solid ${tone === "warn" ? `${SZ.warn}33` : SZ.teal100}`,
    }}
  >
    <Txt sx={{ fontSize: 11.5, color: SZ.ink700, lineHeight: 1.5 }}>{children}</Txt>
  </Flx>
);

const Strong = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 700, color: SZ.ink900 }}>
    {children}
  </Box>
);

const Money = ({ children }) => (
  <Box component="span" sx={{ fontFamily: SZ.mono, fontWeight: 600, color: SZ.teal700 }}>
    {children}
  </Box>
);

/** Small "Estimated" tag beside a label. */
export const EstimatedTag = () => (
  <Box
    component="span"
    sx={{
      ml: "6px",
      px: "6px",
      py: "1px",
      borderRadius: "4px",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: ".03em",
      textTransform: "uppercase",
      background: SZ.warnBg,
      color: SZ.warn,
      verticalAlign: "middle",
    }}
  >
    Estimated
  </Box>
);

/**
 * Interest reserve for ONE property.
 *
 * @param name     the property's field path (`subjectProperties[0]`)
 * @param index    its position, matched against the response's 1-based `property`
 * @param result   the last priced response (may be null)
 * @param askFunding  the product carries construction money (Fix and Flip /
 *                 Ground Up), where the funding answer is always owed
 */
export function InterestReserveFields({ name, index, result, askFunding }) {
  const { input: months } = useField(`${name}.interestReserveMonths`, {
    subscription: { value: true },
  });
  const { input: financed } = useField(`${name}.financedReserve`, {
    subscription: { value: true },
  });
  const hasMonths = !isBlank(months.value);
  const funding = answered(financed.value) ? financed.value : null;

  const summary = result?.interestReserve || null;
  const requiredMonths = summary?.requiredMonths;
  const requiredAmount = summary?.requiredAmount;
  const derived = (result?.derivedInterestReserves || []).find(
    (r) => r?.property === index + 1,
  );

  return (
    <>
      <SubLabel note="months in, dollars calculated when priced">Interest reserve</SubLabel>
      <Grid cols={2}>
        <Field2
          label="Interest reserve months"
          tip="Leave on Required months to use the months the guidelines require for the borrower class. The dollars are monthly interest at the full coupon (30/360) on the loan before the reserve."
        >
          <RffSelect
            clearable
            name={`${name}.interestReserveMonths`}
            options={optionsInterestReserveMonths}
          />
        </Field2>
        {/* REQUIRED, but not a gate: an unanswered reserve still prices (as
            borrower cash, or not applied at all) and comes back as a dataCheck. */}
        <Field2
          label="Reserve funded by"
          required={askFunding || hasMonths}
          tip="A financed reserve is part of the initial loan amount. A borrower-paid reserve counts toward required liquidity."
        >
          <RffBoolSelect
            name={`${name}.financedReserve`}
            options={optionsFinancedReserve}
            placeholder="Not stated"
          />
        </Field2>
      </Grid>

      {requiredMonths != null && requiredAmount != null && (
        <Note>
          <Strong>
            Interest reserve — {requiredMonths} month{requiredMonths === 1 ? "" : "s"} required:
          </Strong>{" "}
          <Money>{fmtMoney(requiredAmount)}</Money>
          {summary?.monthlyInterest != null && (
            <> ({fmtMoney(summary.monthlyInterest)} per month)</>
          )}
          .
        </Note>
      )}

      {funding == null && hasMonths && (
        <Note tone="warn">
          <Strong>State how the reserve is funded.</Strong> Until it is answered the
          reserve is priced as borrower cash.
        </Note>
      )}
      {funding == null && !hasMonths && askFunding && (
        <Note tone="warn">
          <Strong>State how the reserve is funded.</Strong> No reserve is applied
          until it is answered.
        </Note>
      )}

      {derived && (
        <Note>
          Applied reserve <Money>{fmtMoney(derived.amount)}</Money> — {derived.months} ×{" "}
          {fmtMoney(derived.monthlyInterest)} per month
          {derived.monthsSource === "required_default" ? " (required months)" : ""},{" "}
          {derived.financed
            ? "financed inside the initial loan amount."
            : "paid by the borrower at closing."}
        </Note>
      )}
    </>
  );
}

/**
 * Closing costs + assignment fees for ONE property, and whether the closing
 * costs are financed.
 *
 * The funding question appears once there is something to fund: an entered
 * amount, or a purchase (the server estimates 5% of the purchase price when
 * nothing is entered). A financed purchase with no amount gets that estimate
 * WRITTEN onto the property server-side (`closingCostsEstimated: true`); typing
 * a real figure clears the flag here.
 */
export function ClosingCostsFields({ name, index, result, isPurchase }) {
  const form = useForm();
  const { input: costs, meta: costsMeta } = useField(`${name}.closingCosts`, {
    subscription: { value: true, active: true },
  });
  const { input: estimated } = useField(`${name}.closingCostsEstimated`, {
    subscription: { value: true },
  });
  const { input: financed } = useField(`${name}.financedClosingCosts`, {
    subscription: { value: true },
  });
  const { input: price } = useField(`${name}.purchasePrice`, {
    subscription: { value: true },
  });

  // The user typing their own number replaces the server's estimate.
  const isEstimated = estimated.value === true;
  useEffect(() => {
    if (costsMeta.active && isEstimated) {
      form.change(`${name}.closingCostsEstimated`, undefined);
    }
    // Only an edit while the field has focus counts — a reset seeding both
    // values at once must keep the flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costs.value]);

  const hasAmount = Number(costs.value) > 0;
  const showFunding = hasAmount || (isPurchase && Number(price.value) > 0);
  const funding = answered(financed.value) ? financed.value : null;

  const row = (result?.closingCosts?.perProperty || []).find(
    (r) => r?.property === index + 1,
  );
  const serverEstimate =
    !hasAmount && row && /estimated/i.test(row.basis || "") ? row.closingCosts : null;

  return (
    <>
      <SubLabel>Costs at closing</SubLabel>
      <Grid cols={2}>
        <Field2
          label={
            <>
              Closing costs
              {isEstimated && hasAmount && <EstimatedTag />}
            </>
          }
          tip={
            isPurchase
              ? "Title, attorney, recording and transfer costs to take title. Left blank, 5% of the purchase price is used as an estimate."
              : "Title, attorney, recording and transfer costs to take title."
          }
        >
          <RffMoney name={`${name}.closingCosts`} placeholder="0" />
        </Field2>
        <Field2
          label="Assignment fees"
          tip="Assignment of the purchase contract, where there is one. Always paid by the borrower at closing."
        >
          <RffMoney name={`${name}.assignmentFees`} placeholder="0" />
        </Field2>
        {showFunding && (
          <Field2
            label="Closing costs funded by"
            required
            tip="Financed closing costs are part of the initial loan amount. Borrower-paid closing costs count toward required liquidity."
          >
            <RffBoolSelect
              name={`${name}.financedClosingCosts`}
              options={optionsFinancedClosingCosts}
              placeholder="Not stated"
            />
          </Field2>
        )}
      </Grid>

      {isEstimated && hasAmount && (
        <Note tone="warn">
          This is an <Strong>estimate</Strong> (5% of the purchase price). Enter the
          actual closing costs to replace it.
        </Note>
      )}
      {serverEstimate != null && (
        <Note>
          Estimated closing costs <Money>{fmtMoney(serverEstimate)}</Money> — 5% of the
          purchase price. Enter the actual amount to replace it.
        </Note>
      )}
      {showFunding && funding == null && (
        <Note tone="warn">
          <Strong>State how closing costs are funded.</Strong> Until it is answered
          they are priced as borrower cash at closing.
        </Note>
      )}
    </>
  );
}

/**
 * Financed closing costs across the form's properties — the running estimate's
 * mirror of `LoanBaseModel.financed_closing_costs_of`, plus the server's 5%
 * estimate for a financed purchase with nothing entered. Part of the initial
 * loan amount (sizersShared/sizerAmounts adds it there in leverage mode).
 */
export function financedClosingCostsEstimate(props, isPurchase) {
  return (props || []).filter(Boolean).reduce((sum, p) => {
    if (p.financedClosingCosts !== true) return sum;
    const entered = Number(p.closingCosts) || 0;
    if (entered) return sum + entered;
    const price = Number(p.purchasePrice) || 0;
    return sum + (isPurchase && price ? Math.round(0.05 * price * 100) / 100 : 0);
  }, 0);
}

/**
 * A stored (server-normalized) property back into form values. The server
 * writes the reserve DOLLARS onto `interestReserves`; the form never asks for
 * them (months are the input), and a seeded amount would stop the required-
 * months reserve from being re-derived on the next price.
 */
export const propertyFromStored = (p) => {
  if (!p || typeof p !== "object") return p;
  const { interestReserves, ...rest } = p;
  return rest;
};

/**
 * `sizingInput` for the price payload — the field the user drove, posted only
 * when that field has a value (pricing-scenario-sizers.md, 2026-10-19). With
 * 'leverage' the server re-derives the amount and grosses it up by a financed
 * reserve; with 'amount' the entered base is taken as already inclusive.
 */
export function sizingInputFor(values, sizeBy) {
  if (sizeBy === "amount" && !isBlank(values?.baseLoanAmount)) return "amount";
  if (sizeBy !== "amount" && !isBlank(values?.sizerInitialRequestedLeverage)) return "leverage";
  return undefined;
}
